/**
 * Email Test Component
 * 
 * This component provides a user interface to test email sending functionality
 * using the Resend API. It includes form validation, error handling, and 
 * visual feedback for the testing process.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { sendTestEmail } from '@/utils/emailService';
import { Label } from '@/components/ui/label';

// Email validation regex pattern
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * A component for testing email functionality
 */
const EmailTestComponent: React.FC = () => {
  // State variables
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  /**
   * Handles form submission
   */
  const handleSendTest = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setEmailSent(false);
    
    // Validate email
    if (!email.trim()) {
      setValidationError('Email address is required');
      return;
    }
    
    if (!EMAIL_REGEX.test(email)) {
      setValidationError('Please enter a valid email address');
      return;
    }
    
    setValidationError(null);
    setIsLoading(true);
    
    try {
      // Send test email
      const result = await sendTestEmail(email);
      
      if (result.success) {
        setEmailSent(true);
        setError(null);
      } else {
        setError(typeof result.error === 'string' ? result.error : 'Failed to send email');
        setEmailSent(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setEmailSent(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Testing
        </CardTitle>
        <CardDescription>
          Send a test email using Resend API to verify your configuration
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <form onSubmit={handleSendTest}>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="email">Recipient Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter recipient email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-invalid={validationError ? "true" : "false"}
                aria-describedby={validationError ? "email-error" : undefined}
              />
              {validationError && (
                <p id="email-error" className="text-sm text-red-500 mt-1">
                  {validationError}
                </p>
              )}
            </div>
          </div>
          
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {emailSent && (
            <Alert className="mt-4 bg-green-50 text-green-800 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>Test email has been sent successfully!</AlertDescription>
            </Alert>
          )}
          
          <Button 
            type="submit" 
            className="w-full mt-4" 
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Test Email'}
          </Button>
        </form>
      </CardContent>
      
      <CardFooter className="flex justify-between flex-col sm:flex-row text-xs text-muted-foreground">
        <p>Make sure your Resend API key is correctly configured</p>
      </CardFooter>
    </Card>
  );
};

export default EmailTestComponent;
